import { ThreeDot } from "react-loading-indicators";
import { useState } from "react";
import { apiClient } from "../../../../api";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";
import { toast } from "react-toastify";
import { PlusIcon, TrashIcon } from "lucide-react";

const pageLabel = {
	title: "Housekeeping QR Codes",
};
function rangesDoNotOverlap(ranges) {
	if (!Array.isArray(ranges) || ranges.length <= 1) {
		return true;
	}

	const sorted = [...ranges].sort((a, b) => a.rangeStart - b.rangeStart);

	for (let i = 1; i < sorted.length; i++) {
		const prev = sorted[i - 1];
		const curr = sorted[i];
		if (curr.rangeStart <= prev.rangeEnd) {
			return false;
		}
	}
	return true;
}

const HousekeepingQR = () => {
	const [ranges, setRanges] = useState([{ rangeStart: "", rangeEnd: "" }]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const { selectedPropertyId } = useProperty();

	const handleGenerate = async () => {
		for (const range of ranges) {
			const start = parseInt(range.rangeStart, 10);
			const end = parseInt(range.rangeEnd, 10);
			console.log(start, end);
			if (isNaN(start) || isNaN(end)) {
				setError("Please enter valid room numbers.");
				return;
			}
			if (start > end) {
				setError(
					"Start room number must be less than or equal to end room number.",
				);
				return;
			}
			if (start <= 0 || end <= 0) {
				setError("Room numbers must be positive integers.");
				return;
			}
		}
		if (!rangesDoNotOverlap(ranges)) {
			setError("Room ranges must not overlap.");
			return;
		}

		setError("");

		try {
			setLoading(true);
			const response = await apiClient.post(
				`/housekeeping/${selectedPropertyId}/sendEmail`,
				{ ranges: ranges },
			);
			toast.success(
				"QR codes are being generated and will be sent to your email shortly.",
			);
		} catch (err) {
			setError(
				"An error occurred while generating QR codes. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};
	const onRangeStartChange = (value, index) => {
		const updatedRanges = [...ranges];
		updatedRanges[index].rangeStart = value;
		setRanges(updatedRanges);
	};
	const onRangeEndChange = (value, index) => {
		const updatedRanges = [...ranges];
		updatedRanges[index].rangeEnd = value;
		setRanges(updatedRanges);
	};

	return (
		<div className="bg-gray-100 min-h-screen p-12 flex justify-center">
			<div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 w-full">
				{/* Header */}
				<h2 className="text-2xl font-semibold text-gray-900 mb-6">
					{pageLabel.title}
				</h2>

				{/* Range Inputs */}
				{ranges.map((range, index) => (
					<div className="flex gap-6 mb-4 items-center" key={index}>
						<div>
							<label
								htmlFor="rangeStart"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Start Room
							</label>
							<input
								name="rangeStart"
								type="number"
								placeholder="e.g. 101"
								value={range.rangeStart}
								onChange={(e) => onRangeStartChange(e.target.value, index)}
								className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
						</div>

						<div>
							<label
								htmlFor="rangeEnd"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								End Room
							</label>
							<input
								name="rangeEnd"
								type="number"
								value={range.rangeEnd}
								onChange={(e) => onRangeEndChange(e.target.value, index)}
								placeholder="e.g. 120"
								className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
						</div>
						<div>
							{ranges.length > 1 && (
								<button
									onClick={() => {
										const updatedRanges = ranges.filter((_, i) => i !== index);
										setRanges(updatedRanges);
									}}
									className="mt-6 text-red-600 hover:text-red-800 p-2"
								>
									<TrashIcon className="h-5 w-5" />
								</button>
							)}
						</div>
					</div>
				))}
				{/* Error */}
				{error && <p className="text-sm text-red-600 mb-4">{error}</p>}

				{/* Add Range Button */}
				<div className="flex justify-start mb-4">
					<button
						onClick={() =>
							setRanges([...ranges, { rangeStart: "", rangeEnd: "" }])
						}
						className="px-4 py-2 rounded-md bg-green-600 text-white font-medium hover:bg-green-700 flex items-center"
					>
						{" "}
						<PlusIcon className="h-5 w-5 inline-block mr-2" />
						Add Rooms
					</button>
				</div>

				{/* Action */}
				<div className="flex justify-start">
					<button
						onClick={handleGenerate}
						disabled={loading}
						className="px-6 py-2 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
					>
						Send QR Codes via Email
					</button>
				</div>

				{/* Loader */}
				{loading && (
					<div className="flex justify-center mt-6">
						<ThreeDot
							width={50}
							height={50}
							color="#4F46E5"
							variant="brick-stack"
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default HousekeepingQR;
